import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import WorkCard from '@/components/WorkCard';
import { categories, works } from '@/mocks/doujin';

const statusFilters = ['全部', '连载中', '汉化完成', '即将开坑'];

export default function Updates() {
  useSeo({
    title: '全部更新流 - 今日同人库更新·最新汉化·连载进度',
    description:
      '浏览今日同人库全部更新流，按主题分类与连载状态筛选最新汉化、连载中与新作预告，掌握今日同人库今日更新与最新章节进度。',
    keywords: '今日同人库更新,今日同人库今日更新,最新汉化,同人连载',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState('');
  const activeCat = searchParams.get('cat') || '全部';
  const activeStatus = searchParams.get('status') || '全部';

  const filtered = useMemo(() => {
    return works.filter((w) => {
      const matchCat = activeCat === '全部' || w.category === activeCat;
      const matchStatus = activeStatus === '全部' || w.status === activeStatus;
      const matchKw = !keyword || w.title.includes(keyword) || w.tags.some((t) => t.includes(keyword));
      return matchCat && matchStatus && matchKw;
    });
  }, [activeCat, activeStatus, keyword]);

  const setCat = (slug: string) => {
    const next = { ...Object.fromEntries(searchParams), cat: slug };
    if (slug === '全部') delete next.cat;
    setSearchParams(next);
  };
  const setStatus = (s: string) => {
    const next = { ...Object.fromEntries(searchParams), status: s };
    if (s === '全部') delete next.status;
    setSearchParams(next);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '今日同人库全部更新流',
    inLanguage: 'zh-CN',
    url: 'https://gzsxkjjs.cn/updates',
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="bg-secondary-50/70 px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <nav className="text-sm text-foreground-500 mb-3" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">全部更新</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">全部更新流</h1>
          <p className="mt-3 text-foreground-500 text-sm md:text-base max-w-2xl">
            今日同人库今日更新、最新汉化与新作连载全量汇总。按分类与连载状态快速筛选，追更效率加倍。
          </p>

          <div className="mt-6 flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品标题或标签…"
                className="w-full h-11 pl-11 pr-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {statusFilters.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeStatus === s ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCat('全部')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeCat === '全部' ? 'bg-secondary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-secondary-300'
              }`}
            >
              全部分类
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setCat(c.slug)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCat === c.slug ? 'bg-secondary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-secondary-300'
                }`}
              >
                <i className={c.icon}></i>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 max-w-7xl mx-auto py-10">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-foreground-500">
            共 <span className="font-semibold text-primary-700">{filtered.length}</span> 部作品
          </p>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((w) => (
              <WorkCard key={w.slug} work={w} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <i className="ri-inbox-line text-5xl text-background-300"></i>
            <p className="mt-4 text-foreground-500">没有找到匹配的作品，试试调整筛选条件</p>
          </div>
        )}
      </div>
    </>
  );
}